import React from 'react'
import { XMarkIcon, BookOpenIcon } from '@heroicons/react/24/outline'
import { formatMarkdown } from '../utils/markdownParser'

interface KnowledgeArticle {
  id: string
  title: string
  content: string
  excerpt?: string
  category?: {
    id: string
    name: string
  }
  author: {
    firstName: string
    lastName: string
  }
  updatedAt: string
}

interface HelpModalProps {
  isOpen: boolean
  onClose: () => void
  article: KnowledgeArticle | null
  isLoading: boolean
  pageIdentifier: string
  pageTitle?: string
}

const HelpModal: React.FC<HelpModalProps> = ({
  isOpen,
  onClose,
  article,
  isLoading,
  pageIdentifier,
  pageTitle
}) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <BookOpenIcon className="h-6 w-6 text-blue-600" />
                <h3 className="text-lg font-medium text-gray-900" id="modal-title">
                  {article ? article.title : `${pageTitle || pageIdentifier} - Help`}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="mt-4 max-h-[70vh] overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : article ? (
                <div>
                  {article.excerpt && (
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                      <p className="text-sm text-blue-700">{article.excerpt}</p>
                    </div>
                  )}

                  <div
                    className="prose prose-sm max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: formatMarkdown(article.content || '') }}
                  />

                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div>
                        {article.category && (
                          <span className="inline-block bg-gray-100 px-2 py-1 rounded mr-2">
                            {article.category.name}
                          </span>
                        )}
                        <span>
                          By {article.author.firstName} {article.author.lastName}
                        </span>
                      </div>
                      <span>
                        Updated {new Date(article.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No documentation available</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Documentation for this page hasn't been created yet.
                  </p>
                  <p className="mt-2 text-xs text-gray-400">
                    Contact your administrator to add help documentation.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HelpModal
